import { isProductKey, type ProductKey } from "../../../shared/products/keys";

/**
 * Last-used product per instance (spec §6, D8): the stored half of the active product
 * (useActiveProduct). localStorage only; a server-side preference is Phase 3.
 *
 * A UI convenience, never access: it is honoured only for a product the instance has and the user
 * holds a role in, and anything that is not a product key reads as no preference. Only the product
 * key is stored (no PII). Storage that throws (private mode, blocked site data, a full store) reads
 * as no preference and writes nothing.
 */
const keyFor = (instanceId: string) => `skyhire:lastProduct:${instanceId}`;

/** Components drawing the active product, told when a write changes it (this tab only). */
const listeners = new Set<() => void>();
const notify = () => {
  for (const listener of [...listeners]) listener();
};

/**
 * A product the user is in for this page session only, never persisted (fix round 1, ruling c):
 * HomeRoute shows hire when the stored product's landing FAILED (a 5xx, the network) rather than
 * refused, and the shell must say hire too, but one failure must not cost the user their choice.
 * Cleared by any stored write (a switch, a product's own page), and gone on reload.
 */
const sessionProducts = new Map<string, ProductKey>();

export function setSessionProduct(instanceId: string, key: ProductKey): void {
  if (!isProductKey(key) || sessionProducts.get(instanceId) === key) return;
  sessionProducts.set(instanceId, key);
  notify();
}

/** The product the user is in on this instance: this session's, else the stored choice. */
export function readActiveChoice(instanceId: string): ProductKey | null {
  return sessionProducts.get(instanceId) ?? readLastProduct(instanceId);
}

export function clearSessionProductsForTests(): void {
  sessionProducts.clear();
}

export function subscribeLastProduct(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

export function readLastProduct(instanceId: string): ProductKey | null {
  try {
    const value = window.localStorage.getItem(keyFor(instanceId));
    return isProductKey(value) ? value : null;
  } catch {
    return null;
  }
}

export function writeLastProduct(instanceId: string, key: ProductKey): void {
  // Typed, and checked again at runtime: nothing but a product key is ever written.
  if (!isProductKey(key)) return;
  try {
    window.localStorage.setItem(keyFor(instanceId), key);
  } catch {
    // Private mode or a full store: the switch still navigates; only the memory is lost.
  }
  sessionProducts.delete(instanceId);
  notify();
}
